import { FieldPath, FieldValues, get, PathValue, useController } from 'react-hook-form';
import { FormCheckboxProps, FormCheckbox } from '@britishcouncil/react-admin';

import { useFormContext } from './FormContext';
import { ReadonlyFormValue } from './ReadonlyFormValue';
import { RHFormFieldProps } from './types';
import { Form } from 'semantic-ui-react';

type RHFormCheckboxProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = RHFormFieldProps<FormCheckboxProps<boolean>, TFieldValues, TName> & {
  onChange?: (value: PathValue<TFieldValues, TName>) => void;
  header?: string;
  inline?: boolean;
  readonlyMode?: boolean;
};

export const RHFormCheckbox = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  shouldUnregister,
  defaultValue,
  label,
  onChange,
  header,
  inline,
  readonlyMode,
  ...props
}: RHFormCheckboxProps<TFieldValues, TName>) => {
  const { mode } = useFormContext();
  const { field, formState } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const handleOnChange = (value: PathValue<TFieldValues, TName>) => {
    field.onChange(value);
    onChange?.(value);
  };

  if ((mode === 'read' || readonlyMode) && !inline) {
    return (
      <ReadonlyFormValue
        label={label}
        value={field.value}
        renderValue={(value) => (value ? 'Yes' : 'No')}
      />
    );
  }

  return (
    <Form.Group grouped>
      {!inline && <label>{header ?? '\u00A0'}</label>}
      <FormCheckbox
        {...props}
        label={label}
        value={field.value}
        name={field.name}
        onBlur={field.onBlur}
        onChange={handleOnChange}
        touched
        readOnly={mode === 'read' || readonlyMode} //readonly state for inline mode
        error={get(formState.errors, field.name)?.message}
      />
    </Form.Group>
  );
};
